/**
 * Volume + OBV Breakout Scanner view (Investment Ideas -- screening only).
 * Rendered as one of `MarketOpportunitiesPage`'s flat-nav views
 * (`view=volume-obv-breakout`), same embedding convention as
 * `Rsi2PullbackPage`. Read-only: never triggers a calculation, never places
 * an order, never touches portfolio holdings. Raw OBV is deliberately never
 * displayed -- only OBV confirmation / trend / new-high flags.
 */
import { useCallback, useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  FormControl,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TableSortLabel,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import volumeObvBreakoutService, {
  type VolumeObvBreakoutListParams,
} from "@/services/volumeObvBreakoutService";
import type { VolumeObvBreakoutRow } from "@/types";
import { extractErrorMessage } from "@/utils/errors";

const STATUS_COLORS: Record<string, string> = {
  STRONG_BREAKOUT: "#1B5E20",
  BREAKOUT_CANDIDATE: "#2E7D32",
  FAILED_BREAKOUT: "#D32F2F",
  EXPIRED: "#9E9E9E",
};

type SortField = NonNullable<VolumeObvBreakoutListParams["sort_by"]>;

function fixed(value: number | null | undefined, digits = 2): string {
  return value === null || value === undefined ? "—" : value.toLocaleString("en-IN", { minimumFractionDigits: digits, maximumFractionDigits: digits });
}

function dateLabel(value: string | null | undefined): string {
  if (!value) return "—";
  const [y, m, d] = value.slice(0, 10).split("-");
  return `${d}/${m}/${y.slice(2)}`;
}

function smaChip(label: string, status: string) {
  const color = status === "ABOVE" ? "success" : status === "BELOW" ? "error" : "default";
  return (
    <Chip
      size="small"
      variant="outlined"
      color={color as "success" | "error" | "default"}
      label={`${label}: ${status === "ABOVE" ? "Above" : status === "BELOW" ? "Below" : "n/a"}`}
    />
  );
}

function isWeekend(iso: string): boolean {
  const day = new Date(`${iso}T00:00:00`).getDay();
  return day === 0 || day === 6;
}

export function VolumeObvBreakoutPage() {
  const [searchParams] = useSearchParams();
  const initialSymbol = searchParams.get("symbol") ?? "";

  const [rows, setRows] = useState<VolumeObvBreakoutRow[]>([]);
  const [total, setTotal] = useState(0);
  const [signalDate, setSignalDate] = useState<string | null>(null);
  const [latestScanned, setLatestScanned] = useState<string | null>(null);
  const [latestCompleted, setLatestCompleted] = useState<string | null>(null);
  const [disclaimer, setDisclaimer] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(50);
  const [sortBy, setSortBy] = useState<SortField>("rvol");
  const [sortOrder, setSortOrder] = useState<"asc" | "desc">("desc");

  // Filters ("" / "all" = unset)
  const [dateFilter, setDateFilter] = useState("");
  const [status, setStatus] = useState("all");
  const [marketCap, setMarketCap] = useState("all");
  const [minRvol, setMinRvol] = useState("");
  const [maxRvol, setMaxRvol] = useState("");
  const [obv, setObv] = useState("all");
  const [liquidity, setLiquidity] = useState("all");
  const [review, setReview] = useState("all");
  const [age, setAge] = useState("all");
  const [search, setSearch] = useState(initialSymbol);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params: VolumeObvBreakoutListParams = {
        page: page + 1,
        page_size: pageSize,
        sort_by: sortBy,
        sort_order: sortOrder,
      };
      if (dateFilter) params.signal_date = dateFilter;
      if (status !== "all") params.status = [status];
      if (marketCap !== "all") params.market_cap = marketCap as VolumeObvBreakoutListParams["market_cap"];
      if (minRvol !== "" && !Number.isNaN(Number(minRvol))) params.min_rvol = Number(minRvol);
      if (maxRvol !== "" && !Number.isNaN(Number(maxRvol))) params.max_rvol = Number(maxRvol);
      if (obv !== "all") params.obv_confirmed = obv === "yes";
      if (liquidity !== "all") params.liquidity_ok = liquidity === "pass";
      if (review !== "all") params.review_required = review === "yes";
      if (age !== "all") params.max_age_sessions = Number(age);
      if (search.trim()) params.search = search.trim();
      const response = await volumeObvBreakoutService.list(params);
      setRows(response.items);
      setTotal(response.meta.total);
      setSignalDate(response.signal_date ?? null);
      setLatestScanned(response.latest_scanned_date ?? null);
      setLatestCompleted(response.latest_completed_session ?? null);
      setDisclaimer(response.disclaimer);
    } catch (err) {
      setError(extractErrorMessage(err));
    } finally {
      setLoading(false);
    }
  }, [page, pageSize, sortBy, sortOrder, dateFilter, status, marketCap, minRvol, maxRvol, obv, liquidity, review, age, search]);

  useEffect(() => {
    void load();
  }, [load]);

  // Why the chosen date can't have results (shown under the Date field).
  const dateProblem: string | null = !dateFilter
    ? null
    : latestCompleted && dateFilter > latestCompleted
      ? `Not completed yet — latest completed session is ${dateLabel(latestCompleted)}`
      : isWeekend(dateFilter)
        ? "Weekend — not a trading day"
        : null;

  const hasOtherFilters =
    status !== "all" || marketCap !== "all" || minRvol !== "" || maxRvol !== "" || obv !== "all" ||
    liquidity !== "all" || review !== "all" || age !== "all" || search.trim() !== "";
  let emptyReason: string;
  if (latestScanned === null) {
    emptyReason =
      "The scanner has not produced any signals yet. It runs after each end-of-day import; an admin can also run it manually.";
  } else if (dateFilter && latestCompleted && dateFilter > latestCompleted) {
    emptyReason = `${dateLabel(dateFilter)} has not completed yet (latest completed session: ${dateLabel(latestCompleted)}).`;
  } else if (dateFilter && dateFilter !== latestScanned) {
    emptyReason = `No stored results for ${dateLabel(dateFilter)}. The latest scanned session is ${dateLabel(latestScanned)}.`;
  } else if (hasOtherFilters) {
    emptyReason = "No stocks match the current filters for this session — try resetting some filters.";
  } else {
    emptyReason = "No stock met the breakout rules for this session.";
  }

  const activeFilters: { key: string; label: string; clear: () => void }[] = [];
  if (dateFilter) activeFilters.push({ key: "date", label: `Date: ${dateLabel(dateFilter)}`, clear: () => setDateFilter("") });
  if (status !== "all") activeFilters.push({ key: "status", label: `Status: ${status.replace(/_/g, " ").toLowerCase()}`, clear: () => setStatus("all") });
  if (marketCap !== "all") activeFilters.push({ key: "cap", label: `Cap: ${marketCap.replace("_CAP", "").toLowerCase()}`, clear: () => setMarketCap("all") });
  if (minRvol !== "") activeFilters.push({ key: "minr", label: `RVOL ≥ ${minRvol}`, clear: () => setMinRvol("") });
  if (maxRvol !== "") activeFilters.push({ key: "maxr", label: `RVOL ≤ ${maxRvol}`, clear: () => setMaxRvol("") });
  if (obv !== "all") activeFilters.push({ key: "obv", label: `OBV confirmed: ${obv}`, clear: () => setObv("all") });
  if (liquidity !== "all") activeFilters.push({ key: "liq", label: `Liquidity: ${liquidity}`, clear: () => setLiquidity("all") });
  if (review !== "all") activeFilters.push({ key: "rev", label: `Review flag: ${review}`, clear: () => setReview("all") });
  if (age !== "all") activeFilters.push({ key: "age", label: `Age ≤ ${age} sessions`, clear: () => setAge("all") });
  if (search.trim()) activeFilters.push({ key: "sym", label: `Symbol: ${search.trim()}`, clear: () => setSearch("") });

  const resetFilters = () => {
    setDateFilter(""); setStatus("all"); setMarketCap("all"); setMinRvol(""); setMaxRvol("");
    setObv("all"); setLiquidity("all"); setReview("all"); setAge("all"); setSearch("");
    setPage(0);
  };

  const handleSort = (field: SortField) => {
    setSortOrder((prev) => (sortBy === field ? (prev === "asc" ? "desc" : "asc") : "desc"));
    setSortBy(field);
    setPage(0);
  };

  const resetPage = <T,>(setter: (value: T) => void) => (value: T) => {
    setter(value);
    setPage(0);
  };

  const sortHeader = (field: SortField, label: string, align: "left" | "right" = "left", hint?: string) => (
    <TableCell align={align} sx={{ fontWeight: 700, whiteSpace: "nowrap" }} sortDirection={sortBy === field ? sortOrder : false}>
      <Tooltip title={hint ?? ""} disableHoverListener={!hint}>
        <span>
          <TableSortLabel active={sortBy === field} direction={sortBy === field ? sortOrder : "desc"} onClick={() => handleSort(field)}>
            {label}
          </TableSortLabel>
        </span>
      </Tooltip>
    </TableCell>
  );

  const select = (label: string, value: string, onChange: (v: string) => void, options: [string, string][], minWidth = 130) => (
    <FormControl size="small" sx={{ minWidth }}>
      <InputLabel id={`vo-${label.replace(/\s+/g, "-")}`}>{label}</InputLabel>
      <Select labelId={`vo-${label.replace(/\s+/g, "-")}`} label={label} value={value} onChange={(e) => onChange(e.target.value)}>
        {options.map(([v, l]) => (
          <MenuItem key={v} value={v}>
            {l}
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  );

  return (
    <Box>
      <Typography variant="subtitle1" fontWeight={700}>
        Volume + OBV Breakout Scanner
      </Typography>
      <Typography variant="caption" color="text.secondary" display="block" mb={1.5}>
        Daily breakouts above the prior 20-session high, confirmed by relative volume and On-Balance Volume — completed
        daily candles only. Screening information only.
        {signalDate ? ` Showing session ${dateLabel(signalDate)}.` : ""}
      </Typography>

      <Typography variant="caption" color="text.secondary" display="block" mb={1.5}>
        <b>Review required</b> = breakout-day RVOL above 3x (unusually heavy volume — a prompt to check news, not a rejection).
        Signals are listed for up to 20 sessions after the breakout; older ones are marked aged.
      </Typography>

      <Paper variant="outlined" sx={{ borderRadius: 3, p: 1.5, mb: 2 }}>
        <Stack direction="row" flexWrap="wrap" gap={1.5} alignItems="center">
          <TextField
            size="small"
            type="date"
            label="Date"
            InputLabelProps={{ shrink: true }}
            // Sessions after the latest completed one can never have a
            // signal (completed candles only) -- the picker won't offer them.
            inputProps={{ max: latestCompleted ?? undefined }}
            value={dateFilter}
            onChange={(e) => resetPage(setDateFilter)(e.target.value)}
            error={dateProblem !== null}
            helperText={dateProblem ?? undefined}
          />
          {select("Status", status, resetPage(setStatus), [
            ["all", "All statuses"],
            ["STRONG_BREAKOUT", "Strong Breakout"],
            ["BREAKOUT_CANDIDATE", "Breakout Candidate"],
            ["FAILED_BREAKOUT", "Failed Breakout"],
            ["EXPIRED", "Expired"],
          ], 170)}
          {select("Market cap", marketCap, resetPage(setMarketCap), [
            ["all", "All"],
            ["LARGE_CAP", "Large"],
            ["MID_CAP", "Mid"],
            ["SMALL_CAP", "Small"],
            ["MICRO_CAP", "Micro"],
          ])}
          <TextField size="small" label="Min RVOL" type="number" sx={{ width: 120 }} value={minRvol} onChange={(e) => resetPage(setMinRvol)(e.target.value)} />
          <TextField size="small" label="Max RVOL" type="number" sx={{ width: 125 }} value={maxRvol} onChange={(e) => resetPage(setMaxRvol)(e.target.value)} />
          {select("OBV confirmed", obv, resetPage(setObv), [["all", "Any"], ["yes", "Yes"], ["no", "No"]])}
          {select("Liquidity", liquidity, resetPage(setLiquidity), [["all", "Any"], ["pass", "Pass"], ["fail", "Fail"]])}
          {select("Review flag", review, resetPage(setReview), [["all", "Any"], ["yes", "Review required"], ["no", "No flag"]], 150)}
          {select("Signal age", age, resetPage(setAge), [
            ["all", "Any age"],
            ["0", "Today only"],
            ["5", "Last 5 sessions"],
            ["10", "Last 10 sessions"],
          ], 160)}
          <TextField size="small" label="Symbol" value={search} onChange={(e) => resetPage(setSearch)(e.target.value)} />
          {activeFilters.length > 0 && (
            <Button size="small" onClick={resetFilters}>
              Reset filters
            </Button>
          )}
        </Stack>
        {activeFilters.length > 0 && (
          <Stack direction="row" flexWrap="wrap" gap={0.75} mt={1.25} aria-label="Active filters">
            {activeFilters.map((f) => (
              <Chip key={f.key} size="small" variant="outlined" label={f.label} onDelete={() => { f.clear(); setPage(0); }} />
            ))}
          </Stack>
        )}
      </Paper>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {loading ? (
        <Box display="flex" justifyContent="center" py={6}>
          <CircularProgress />
        </Box>
      ) : rows.length === 0 ? (
        <Paper variant="outlined" sx={{ borderRadius: 3, p: 5, textAlign: "center" }}>
          <Typography variant="body1" gutterBottom>
            No breakout signals for this selection
          </Typography>
          <Typography variant="body2" color="text.secondary" mb={2}>
            {emptyReason}
          </Typography>
          {dateFilter && latestScanned && dateFilter !== latestScanned && (
            <Button variant="outlined" size="small" onClick={() => resetPage(setDateFilter)("")}>
              Show latest scanned session ({dateLabel(latestScanned)})
            </Button>
          )}
        </Paper>
      ) : (
        <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 3 }}>
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow>
                {sortHeader("symbol", "Stock")}
                <TableCell sx={{ fontWeight: 700 }}>Cap</TableCell>
                {sortHeader("status", "Status")}
                {sortHeader("breakout_date", "Breakout Date")}
                <TableCell align="right" sx={{ fontWeight: 700 }}>Breakout Level</TableCell>
                <TableCell align="right" sx={{ fontWeight: 700 }}>Breakout Close</TableCell>
                {sortHeader("pct_above_level", "Now vs Level", "right", "Latest close vs. the breakout level (not the breakout-day close)")}
                {sortHeader("rvol", "RVOL", "right")}
                <TableCell align="right" sx={{ fontWeight: 700 }}>Avg Vol (20d prior)</TableCell>
                <TableCell align="right" sx={{ fontWeight: 700 }}>Volume</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>OBV</TableCell>
                {sortHeader("close_position", "Candle Strength", "right")}
                <TableCell sx={{ fontWeight: 700 }}>Trend Context</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Liquidity</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Updated</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map((row) => (
                <TableRow key={`${row.symbol}-${row.signal_date}`} hover>
                  <TableCell sx={{ maxWidth: 200 }}>
                    <Typography variant="body2" fontWeight={700}>{row.symbol}</Typography>
                    <Typography variant="caption" color="text.secondary" noWrap display="block" title={row.company_name}>{row.company_name}</Typography>
                  </TableCell>
                  <TableCell>{row.market_cap_segment}</TableCell>
                  <TableCell>
                    <Stack spacing={0.5} alignItems="flex-start">
                      <Chip
                        size="small"
                        label={row.status_label}
                        sx={{ bgcolor: STATUS_COLORS[row.status] ?? "#616161", color: "#fff", fontWeight: 600 }}
                      />
                      {row.review_required && (
                        <Tooltip title={row.review_note ?? "Unusually high volume — review before relying on this signal."}>
                          <Chip size="small" color="warning" variant="outlined" label="Review required" />
                        </Tooltip>
                      )}
                      {row.status === "FAILED_BREAKOUT" && row.failure_note && (
                        <Tooltip title={row.failure_note}>
                          <Typography variant="caption" color="error.main">
                            Failed {dateLabel(row.failed_on_date)}
                          </Typography>
                        </Tooltip>
                      )}
                      {(row.age_sessions ?? 0) > 0 && row.status !== "FAILED_BREAKOUT" && (
                        <Tooltip title={`Aged signal — the breakout happened ${row.age_sessions} session(s) before this scan date. The status is still valid but no longer fresh.`}>
                          <Typography variant="caption" color={(row.age_sessions ?? 0) > 5 ? "warning.main" : "text.secondary"}>
                            {row.age_sessions} session(s) old
                          </Typography>
                        </Tooltip>
                      )}
                    </Stack>
                  </TableCell>
                  <TableCell>{dateLabel(row.breakout_date)}</TableCell>
                  <TableCell align="right">{fixed(row.breakout_level)}</TableCell>
                  <TableCell align="right">{fixed(row.breakout_close)}</TableCell>
                  <TableCell align="right">{row.pct_above_level == null ? "—" : `${row.pct_above_level > 0 ? "+" : ""}${fixed(row.pct_above_level)}%`}</TableCell>
                  <TableCell align="right">{row.rvol == null ? "—" : `${fixed(row.rvol)}x`}</TableCell>
                  <TableCell align="right">{fixed(row.avg_volume_prior_20, 0)}</TableCell>
                  <TableCell align="right">{fixed(row.volume, 0)}</TableCell>
                  <TableCell>
                    <Tooltip title={`Mode: ${row.obv_mode}${row.obv_new_high != null ? ` · OBV at ${row.obv_new_high ? "a" : "no"} new 20-session high` : ""}`}>
                      <Box component="span">
                        {row.obv_confirmation}
                        {row.obv_trend ? ` · ${row.obv_trend.toLowerCase()}` : ""}
                      </Box>
                    </Tooltip>
                  </TableCell>
                  <TableCell align="right">{row.close_position == null ? "—" : `${Math.round(row.close_position * 100)}%`}</TableCell>
                  <TableCell>
                    <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
                      {smaChip("SMA50", row.sma50_status)}
                      {smaChip("SMA200", row.sma200_status)}
                    </Stack>
                  </TableCell>
                  <TableCell>{row.liquidity_status}</TableCell>
                  <TableCell sx={{ whiteSpace: "nowrap" }}>{new Date(row.last_updated).toLocaleString("en-IN")}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          <TablePagination
            component="div"
            count={total}
            page={page}
            onPageChange={(_e, p) => setPage(p)}
            rowsPerPage={pageSize}
            rowsPerPageOptions={[25, 50, 100]}
            onRowsPerPageChange={(e) => {
              setPageSize(parseInt(e.target.value, 10));
              setPage(0);
            }}
          />
        </TableContainer>
      )}

      {disclaimer && (
        <Typography variant="caption" color="text.secondary" display="block" mt={2} sx={{ opacity: 0.8 }}>
          {disclaimer}
        </Typography>
      )}
    </Box>
  );
}

export default VolumeObvBreakoutPage;
